import { FlexCol } from '@/components/ui/flex';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UpgradeButton } from '@/components/ui/upgrade-button';
import { useEditableMarkdown } from '@/shared/EditableMarkdown';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import { DEVICE_CHARACTERISTICS } from '@ditto/shared';
import {
  GEOGRAPHY_FLAG_IMAGES,
  GEOGRAPHY_LABELS,
} from '@/pages/RegulatoryWatch/labels';
import { useListSourcesApi } from '@/api/regulatoryWatch';
import { Circle, CircleCheck, Pencil, Plus } from 'lucide-react';
import { type FC, useMemo } from 'react';

export type MarketOption = {
  value: string;
  label: string;
  flag?: string;
};

export function useMarketOptions(): MarketOption[] {
  const { data: sources } = useListSourcesApi();

  return useMemo(() => {
    const PINNED = ['eu', 'us'];

    const all = [
      ...new Set(
        (sources ?? [])
          .map(s => (s.geography === 'gb' ? 'uk' : s.geography))
          .filter(g => g && g !== 'world' && g !== 'unknown')
      ),
    ];
    const pinned = PINNED.filter(g => all.includes(g));
    const rest = all.filter(g => !PINNED.includes(g)).sort();
    const geographies = [...pinned, ...rest];

    return geographies.map(geo => ({
      value: geo.toUpperCase(),
      label: GEOGRAPHY_LABELS[geo] ?? geo,
      flag: GEOGRAPHY_FLAG_IMAGES[geo],
    }));
  }, [sources]);
}

const PUBLIC_GEOGRAPHIES = ['eu', 'us', 'ca', 'br', 'jp', 'au'];

export const PUBLIC_MARKET_OPTIONS: MarketOption[] = PUBLIC_GEOGRAPHIES.map(
  geo => ({
    value: geo.toUpperCase(),
    label: GEOGRAPHY_LABELS[geo] ?? geo,
    flag: GEOGRAPHY_FLAG_IMAGES[geo],
  })
);

export const US_RISK_CLASSIFICATIONS = [
  'US Class I',
  'US Class II',
  'US Class III',
];
export const EU_RISK_CLASSIFICATIONS = [
  'MDR Class I',
  'MDR Class IIa',
  'MDR Class IIb',
  'MDR Class III',
  'IVDR Class A',
  'IVDR Class B',
  'IVDR Class C',
  'IVDR Class D',
];

export const THERAPEUTIC_AREAS = [
  'Cardiovascular',
  'Oncology',
  'Orthopedic',
  'Neurological',
  'Ophthalmic',
  'Dental',
  'Ear, Nose, Throat (ENT)',
  'Respiratory / Anesthesiology',
  'Gastroenterology / Urology',
  'General and Plastic Surgery',
  'Obstetrics / Gynecology',
  'Radiology / Imaging',
  'Clinical Chemistry / Hematology (IVD)',
  'Microbiology / Immunology (IVD)',
  'Pathology / Histology (IVD)',
  'Physical Medicine / Rehabilitation',
  'General Hospital / Patient Monitoring',
];

export const toggleInArray = (arr: string[], val: string) =>
  arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

export type ProfileFormProps = {
  markets: string[];
  onToggleMarket: (market: string) => void;
  riskClassifications: string[];
  onSetRiskClassifications: (value: string[]) => void;
  deviceCharacteristics: string[];
  onSetDeviceCharacteristics: (value: string[]) => void;
  therapeuticAreas: string[];
  onSetTherapeuticAreas: (value: string[]) => void;
  generatedDescription?: string;
  onSetGeneratedDescription?: (value: string) => void;
  onRequestNewRegion?: () => void;
  marketOptions?: MarketOption[];
  hideDescription?: boolean;
};

export const ProfileForm: FC<ProfileFormProps> = ({
  markets,
  onToggleMarket,
  riskClassifications,
  onSetRiskClassifications,
  deviceCharacteristics,
  onSetDeviceCharacteristics,
  therapeuticAreas,
  onSetTherapeuticAreas,
  generatedDescription,
  onSetGeneratedDescription,
  onRequestNewRegion,
  marketOptions: marketOptionsProp,
  hideDescription,
}) => {
  const {
    isEditing: isEditingDescription,
    textareaProps: descriptionTextareaProps,
    onPreviewClick: onDescriptionPreviewClick,
    onPreviewKeyDown: onDescriptionPreviewKeyDown,
  } = useEditableMarkdown({
    value: generatedDescription ?? '',
    onChange: onSetGeneratedDescription ?? (() => {}),
  });

  const allRiskClassifications = [
    ...US_RISK_CLASSIFICATIONS,
    ...EU_RISK_CLASSIFICATIONS,
  ];

  const hookOptions = useMarketOptions();
  const marketOptions = marketOptionsProp ?? hookOptions;

  return (
    <FlexCol gap="4" className="pl-4">
      {/* Regulatory region */}
      <FlexCol gap="2">
        <span className="text-sm font-medium text-neutral-900">
          Regulatory region
        </span>
        <div className="flex flex-wrap gap-2">
          {marketOptions.map(opt => {
            const selected = markets.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggleMarket(opt.value)}
                className={`flex items-center gap-1.5 rounded-lg px-2 py-[3px] min-h-[24px] text-xs font-medium text-neutral-900 transition-colors border ${
                  selected
                    ? 'bg-[#ffe228] border-neutral-900'
                    : 'border-neutral-200'
                }`}
              >
                {selected ? (
                  <CircleCheck className="size-3 shrink-0" />
                ) : (
                  <Circle
                    className="size-3 shrink-0 text-neutral-400"
                    strokeWidth={2}
                  />
                )}
                {opt.flag && (
                  <img
                    src={opt.flag}
                    alt=""
                    className="h-3 w-4 shrink-0 rounded-[1px] object-cover"
                  />
                )}
                {opt.label}
              </button>
            );
          })}
          {onRequestNewRegion && (
            <UpgradeButton onClick={onRequestNewRegion}>
              <Plus className="size-3 shrink-0" />
              Add regulatory region
            </UpgradeButton>
          )}
        </div>
      </FlexCol>

      {/* Risk classification */}
      <div className="border-l border-neutral-200 pl-4">
        <FlexCol gap="2">
          <FlexCol gap="0">
            <span className="text-sm font-medium text-neutral-900">
              Risk classification
            </span>
            <span className="text-xs text-neutral-600">
              The available options depend on the regulatory region selection
              above.
            </span>
          </FlexCol>
          <div className="flex flex-wrap gap-2">
            {allRiskClassifications.map(rc => {
              const selected = riskClassifications.includes(rc);
              const isUSClass = rc.startsWith('US ');
              const isEUClass = rc.startsWith('MDR ') || rc.startsWith('IVDR ');
              const isEnabled =
                (isUSClass && markets.includes('US')) ||
                (isEUClass && markets.includes('EU'));

              const button = (
                <button
                  key={rc}
                  type="button"
                  disabled={!isEnabled}
                  onClick={() =>
                    isEnabled &&
                    onSetRiskClassifications(
                      toggleInArray(riskClassifications, rc)
                    )
                  }
                  className={`flex items-center gap-1.5 rounded-lg px-2 py-[3px] min-h-[24px] text-xs font-medium transition-colors border ${
                    !isEnabled
                      ? 'border-neutral-200 text-neutral-400 cursor-not-allowed'
                      : selected
                        ? 'bg-[#ffe228] border-neutral-900 text-neutral-900'
                        : 'border-neutral-200 text-neutral-900'
                  }`}
                >
                  {selected && isEnabled ? (
                    <CircleCheck className="size-3 shrink-0" />
                  ) : (
                    <Circle
                      className={`size-3 shrink-0 ${isEnabled ? 'text-neutral-400' : 'text-neutral-300'}`}
                      strokeWidth={2}
                    />
                  )}
                  {rc}
                </button>
              );

              if (!isEnabled) {
                return (
                  <Tooltip key={rc}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="top" sideOffset={4}>
                      {isUSClass
                        ? 'Select "United States (FDA)" to enable'
                        : 'Select "European Union (MDR/IVDR)" to enable'}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return button;
            })}
          </div>
        </FlexCol>
      </div>

      {/* Characteristics */}
      <FlexCol gap="2">
        <span className="text-sm font-medium text-neutral-900">
          Characteristics
        </span>
        <div className="flex flex-wrap gap-2">
          {DEVICE_CHARACTERISTICS.map(dc => {
            const selected = deviceCharacteristics.includes(dc.value);
            return (
              <Tooltip key={dc.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() =>
                      onSetDeviceCharacteristics(
                        toggleInArray(deviceCharacteristics, dc.value)
                      )
                    }
                    className={`flex items-center gap-1.5 rounded-lg px-2 py-[3px] min-h-[24px] text-xs font-medium text-neutral-900 transition-colors border ${
                      selected
                        ? 'bg-[#ffe228] border-neutral-900'
                        : 'border-neutral-200'
                    }`}
                  >
                    {selected ? (
                      <CircleCheck className="size-3 shrink-0" />
                    ) : (
                      <Circle
                        className="size-3 shrink-0 text-neutral-400"
                        strokeWidth={2}
                      />
                    )}
                    {dc.value}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4}>
                  {dc.definition}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </FlexCol>

      {/* Therapeutic area */}
      <FlexCol gap="2">
        <span className="text-sm font-medium text-neutral-900">
          Therapeutic area
        </span>
        <div className="flex flex-wrap gap-2">
          {THERAPEUTIC_AREAS.map(ta => {
            const selected = therapeuticAreas.includes(ta);
            return (
              <button
                key={ta}
                type="button"
                onClick={() =>
                  onSetTherapeuticAreas(toggleInArray(therapeuticAreas, ta))
                }
                className={`flex items-center gap-1.5 rounded-lg px-2 py-[3px] min-h-[24px] text-xs font-medium text-neutral-900 transition-colors border ${
                  selected
                    ? 'bg-[#ffe228] border-neutral-900'
                    : 'border-neutral-200'
                }`}
              >
                {selected ? (
                  <CircleCheck className="size-3 shrink-0" />
                ) : (
                  <Circle
                    className="size-3 shrink-0 text-neutral-400"
                    strokeWidth={2}
                  />
                )}
                {ta}
              </button>
            );
          })}
        </div>
      </FlexCol>

      {/* Device description — hidden when products are managed separately */}
      {!hideDescription && (
        <FlexCol gap="1">
          <span className="text-sm font-medium text-neutral-900">
            Device description
          </span>
          {isEditingDescription ? (
            <Textarea
              {...descriptionTextareaProps}
              className="min-h-[100px] w-full resize-none border-neutral-300 overflow-hidden"
              placeholder="Describe your device..."
            />
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={onDescriptionPreviewClick}
              onKeyDown={onDescriptionPreviewKeyDown}
              className="group relative min-h-[100px] w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 cursor-pointer transition-colors hover:border-dashed hover:border-neutral-400 hover:bg-neutral-100/50"
            >
              <Pencil className="absolute top-3 right-3 size-3.5 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100" />
              {generatedDescription ? (
                <MarkdownText size="sm">{generatedDescription}</MarkdownText>
              ) : (
                <span className="text-sm text-neutral-500">
                  Click to add a description...
                </span>
              )}
            </div>
          )}
        </FlexCol>
      )}
    </FlexCol>
  );
};
